import React, { useState, useEffect, useRef } from 'react';
import {
    Typography, Box, Paper, Button, CircularProgress, Snackbar, Alert,
    useMediaQuery, useTheme, Tabs, Tab
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TuneIcon from '@mui/icons-material/Tune';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HubIcon from '@mui/icons-material/Hub';
import MessageIcon from '@mui/icons-material/Message';
import api from '../services/api';
import useImagemBlobUrl from '../hooks/useImagemBlobUrl';
import { buscarCep, cepCompleto, mensagemFalhaCep } from '../services/cep';
import SecaoGeral from './parametros/SecaoGeral';
import SecaoConsultorio from './parametros/SecaoConsultorio';
import SecaoIntegracoes from './parametros/SecaoIntegracoes';
import SecaoMensagens from './parametros/SecaoMensagens';

/** Igual a ParametroSistemaService.MASCARA_SEGREDO: nunca enviada como valor real. */
export const MASCARA_SEGREDO = '********';

const ENDERECO_VAZIO = {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cep: '',
    nomeCidade: '',
    ufEstado: '',
};

export default function Parametros() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [buscandoCep, setBuscandoCep] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [psicologos, setPsicologos] = useState([]);
    // Guarda o objectName do MinIO que veio do cadastro; o que vai para o `src`
    // é o blob buscado pela API (ver useImagemBlobUrl).
    const [logoPreview, setLogoPreview] = useState(null);
    const logoBlobUrl = useImagemBlobUrl('/parametros/logo', logoPreview);
    const logoInputRef = useRef(null);

    const [parametros, setParametros] = useState({
        duracaoPadraoSessaoMinutos: 50,
        horarioInicioExpediente: '08:00',
        horarioFimExpediente: '18:00',
        valorPadraoSessao: '',
        quantidadeBaseSessoes: 4,
        diasToleranciaVencimento: 5,
        textoPadraoContrato: '',
        textoPadraoDeclaracao: '',
        notificarSessaoPerto: true,
        minutosAntecedenciaNotificacao: 30,
        consultorioNome: '',
        consultorioTelefone: '',
        idPsicologo: '',
        diasRenovacao: '',
        cabecalhoDocumento: '',
        googleClientId: '',
        googleClientSecret: '',
        googleRefreshToken: '',
        googleClientSecretConfigurado: false,
        googleRefreshTokenConfigurado: false,
        smtpSenhaConfigurada: false,
        evolutionInstanceName: '',
        emailAtivo: false,
        smtpHost: '',
        smtpPorta: 587,
        smtpUsuario: '',
        smtpSenha: '',
        emailRemetente: '',
        emailNomeRemetente: '',
        smtpStartTls: true,
        smtpSslEnable: false,
        smtpTimeoutMs: 5000,
        urlPublicaBase: '',
        agendaGeracaoAutomatica: true,
        agendaJanelaDias: 60,
        modoFocoEvolucao: true,
        endereco: { ...ENDERECO_VAZIO },
    });

    // Evolution / WhatsApp
    const [evoStatus, setEvoStatus] = useState(null);
    const [evoQrcode, setEvoQrcode] = useState(null);
    const [evoLoading, setEvoLoading] = useState(false);
    const [evoSendData, setEvoSendData] = useState({ numero: '', mensagem: '' });
    const [evoSending, setEvoSending] = useState(false);

    // Teste genérico de notificação (canal + tipo)
    const [testeData, setTesteData] = useState({
        canal: 'EMAIL',
        tipo: 'GENERICO',
        destinatario: '',
        assuntoCustom: '',
        conteudoCustom: '',
        nome: '',
        link: '',
    });
    const [testeSending, setTesteSending] = useState(false);

    useEffect(() => {
        Promise.all([
            // Tela administrativa: o payload de integrações vem do endpoint de
            // administrador e nunca traz segredo — só o indicador "configurado".
            api.get('/parametros/administrativo'),
            api.get('/psicologos'),
        ]).then(([resParam, resPsi]) => {
            const d = { ...resParam.data.comum, ...resParam.data };
            const end = d.consultorioEndereco;
            setParametros({
                duracaoPadraoSessaoMinutos: d.duracaoPadraoSessaoMinutos ?? 50,
                horarioInicioExpediente: d.horarioInicioExpediente?.substring(0, 5) ?? '08:00',
                horarioFimExpediente: d.horarioFimExpediente?.substring(0, 5) ?? '18:00',
                valorPadraoSessao: d.valorPadraoSessao ?? '',
                quantidadeBaseSessoes: d.quantidadeBaseSessoes ?? 4,
                diasToleranciaVencimento: d.diasToleranciaVencimento ?? 5,
                textoPadraoContrato: d.textoPadraoContrato ?? '',
                textoPadraoDeclaracao: d.textoPadraoDeclaracao ?? '',
                notificarSessaoPerto: d.notificarSessaoPerto ?? true,
                minutosAntecedenciaNotificacao: d.minutosAntecedenciaNotificacao ?? 30,
                consultorioNome: d.consultorioNome ?? '',
                consultorioTelefone: d.consultorioTelefone ?? '',
                idPsicologo: d.idPsicologo ?? '',
                diasRenovacao: d.diasRenovacao ?? '',
                cabecalhoDocumento: d.cabecalhoDocumento ?? '',
                googleClientId: d.googleClientId ?? '',
                // Segredos nunca voltam do servidor: campo vazio = mantém o gravado.
                googleClientSecret: '',
                googleRefreshToken: '',
                googleClientSecretConfigurado: d.googleClientSecretConfigurado ?? false,
                googleRefreshTokenConfigurado: d.googleRefreshTokenConfigurado ?? false,
                smtpSenhaConfigurada: d.smtpSenhaConfigurada ?? false,
                evolutionInstanceName: d.evolutionInstanceName ?? '',
                emailAtivo: d.emailAtivo ?? false,
                smtpHost: d.smtpHost ?? '',
                smtpPorta: d.smtpPorta ?? 587,
                smtpUsuario: d.smtpUsuario ?? '',
                smtpSenha: '',
                emailRemetente: d.emailRemetente ?? '',
                emailNomeRemetente: d.emailNomeRemetente ?? '',
                smtpStartTls: d.smtpStartTls ?? true,
                smtpSslEnable: d.smtpSslEnable ?? false,
                smtpTimeoutMs: d.smtpTimeoutMs ?? 5000,
                urlPublicaBase: d.urlPublicaBase ?? '',
                agendaGeracaoAutomatica: d.agendaGeracaoAutomatica ?? true,
                agendaJanelaDias: d.agendaJanelaDias ?? 60,
                modoFocoEvolucao: d.modoFocoEvolucao ?? true,
                endereco: end ? {
                    logradouro: end.logradouro ?? '',
                    numero: end.numero ?? '',
                    complemento: end.complemento ?? '',
                    bairro: end.bairro ?? '',
                    cep: end.cep ? end.cep.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substring(0, 9) : '',
                    nomeCidade: end.cidade?.nome ?? '',
                    ufEstado: end.cidade?.estado?.sigla ?? '',
                } : { ...ENDERECO_VAZIO },
            });
            setPsicologos(resPsi.data ?? []);
            if (d.logoUrl) setLogoPreview(d.logoUrl);
        }).catch(() => {
            mostrarMensagem('Erro ao carregar configurações.', 'error');
        }).finally(() => setLoading(false));
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setParametros(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const formatarCEP = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substring(0, 9);
    };

    const handleEnderecoChange = (e) => {
        const { name, value } = e.target;
        const valorFinal = name === 'cep' ? formatarCEP(value) : value;
        setParametros(prev => ({ ...prev, endereco: { ...prev.endereco, [name]: valorFinal } }));
    };

    // Consulta via services/cep.js: lá ficam o timeout, o { erro: true } que o
    // ViaCEP devolve com HTTP 200 e as mensagens por motivo de falha.
    const handleBuscaCEP = async (e) => {
        const valor = e.target.value;
        setParametros(prev => ({ ...prev, endereco: { ...prev.endereco, cep: valor } }));
        if (!cepCompleto(valor)) return;

        setBuscandoCep(true);
        try {
            const resultado = await buscarCep(valor);
            if (!resultado.ok) {
                mostrarMensagem(mensagemFalhaCep(resultado.motivo),
                    resultado.motivo === 'nao_encontrado' ? 'warning' : 'error');
                return;
            }
            setParametros(prev => ({
                ...prev,
                endereco: {
                    ...prev.endereco,
                    logradouro: resultado.endereco.logradouro,
                    bairro: resultado.endereco.bairro,
                    nomeCidade: resultado.endereco.localidade,
                    ufEstado: resultado.endereco.uf,
                }
            }));
        } finally {
            setBuscandoCep(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...parametros,
                idPsicologo: parametros.idPsicologo || null,
                horarioInicioExpediente: parametros.horarioInicioExpediente + ':00',
                horarioFimExpediente: parametros.horarioFimExpediente + ':00',
                logradouro: parametros.endereco.logradouro || null,
                numero: parametros.endereco.numero || null,
                complemento: parametros.endereco.complemento || null,
                bairro: parametros.endereco.bairro || null,
                cep: parametros.endereco.cep ? parametros.endereco.cep.replace(/\D/g, '') : null,
                nomeCidade: parametros.endereco.nomeCidade || null,
                ufEstado: parametros.endereco.ufEstado || null,
                cabecalhoDocumento: parametros.cabecalhoDocumento || null,
                evolutionInstanceName: parametros.evolutionInstanceName || null,
            };
            // Segredos: só vão no payload quando o usuário digitou algo novo.
            // Campo em branco significa "preserva a credencial atual" — nunca
            // envia máscara, que o servidor também recusaria como valor real.
            ['smtpSenha', 'googleClientSecret', 'googleRefreshToken'].forEach((campo) => {
                const valor = (parametros[campo] ?? '').trim();
                if (!valor || valor === MASCARA_SEGREDO) delete payload[campo];
                else payload[campo] = valor;
            });
            delete payload.googleClientSecretConfigurado;
            delete payload.googleRefreshTokenConfigurado;
            delete payload.smtpSenhaConfigurada;
            delete payload.endereco;
            const { data } = await api.put('/parametros', payload);
            // Limpa os campos de segredo e reflete o estado real devolvido.
            setParametros(prev => ({
                ...prev,
                smtpSenha: '',
                googleClientSecret: '',
                googleRefreshToken: '',
                googleClientSecretConfigurado: data?.googleClientSecretConfigurado ?? prev.googleClientSecretConfigurado,
                googleRefreshTokenConfigurado: data?.googleRefreshTokenConfigurado ?? prev.googleRefreshTokenConfigurado,
                smtpSenhaConfigurada: data?.smtpSenhaConfigurada ?? prev.smtpSenhaConfigurada,
            }));
            mostrarMensagem('Parâmetros salvos com sucesso!');
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao salvar as configurações.';
            mostrarMensagem(msg, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingLogo(true);
        try {
            const form = new FormData();
            form.append('arquivo', file);
            const res = await api.post('/parametros/logo', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setLogoPreview(res.data.logoUrl);
            mostrarMensagem('Logo atualizada com sucesso!');
        } catch {
            mostrarMensagem('Erro ao fazer upload da logo.', 'error');
        } finally {
            setUploadingLogo(false);
            e.target.value = '';
        }
    };

    const mostrarMensagem = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const carregarEvoStatus = async () => {
        try {
            const res = await api.get('/evolution/status');
            setEvoStatus(res.data);
        } catch (err) {
            mostrarMensagem(err.response?.data?.message || 'Erro ao verificar status do WhatsApp.', 'error');
        }
    };

    const carregarEvoQrCode = async () => {
        if (!parametros.evolutionInstanceName?.trim()) {
            mostrarMensagem('Defina o nome da instância e salve antes de gerar o QR Code.', 'warning');
            return;
        }
        setEvoLoading(true);
        try {
            const res = await api.get('/evolution/qrcode');
            setEvoQrcode(res.data);
            await carregarEvoStatus();
        } catch (err) {
            mostrarMensagem(err.response?.data?.message || 'Erro ao gerar QR Code.', 'error');
        } finally {
            setEvoLoading(false);
        }
    };

    const enviarTesteNotificacao = async () => {
        if (!testeData.canal || !testeData.destinatario) {
            mostrarMensagem('Informe canal e destinatário.', 'warning');
            return;
        }
        setTesteSending(true);
        try {
            const variables = {};
            if (testeData.nome) variables.nome = testeData.nome;
            if (testeData.link) variables.link = testeData.link;
            const payload = {
                canal: testeData.canal,
                tipo: testeData.tipo || 'GENERICO',
                destinatario: testeData.destinatario,
                assuntoCustom: testeData.assuntoCustom || null,
                conteudoCustom: testeData.conteudoCustom || null,
                variables,
            };
            const res = await api.post('/notificacoes/teste', payload);
            if (res.data.status === 'ENVIADA') {
                mostrarMensagem(`Notificação enviada (id ${res.data.id}).`);
            } else {
                mostrarMensagem(`Falha: ${res.data.mensagemErro || 'erro desconhecido'}`, 'error');
            }
        } catch (err) {
            mostrarMensagem(err.response?.data?.message || 'Erro ao enviar teste.', 'error');
        } finally {
            setTesteSending(false);
        }
    };

    const enviarMensagemTeste = async () => {
        if (!evoSendData.numero || !evoSendData.mensagem) {
            mostrarMensagem('Informe número e mensagem.', 'warning');
            return;
        }
        setEvoSending(true);
        try {
            await api.post('/evolution/enviar-teste', evoSendData);
            mostrarMensagem('Mensagem enviada com sucesso!');
            setEvoSendData({ numero: '', mensagem: '' });
        } catch (err) {
            mostrarMensagem(err.response?.data?.message || 'Erro ao enviar mensagem.', 'error');
        } finally {
            setEvoSending(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, pb: 10 }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333', mb: 3 }}>
                Parâmetros do Sistema
            </Typography>

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid #eee',
                        bgcolor: '#fafafa',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56 },
                        '& .Mui-selected': { color: '#1ABB9C !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#1ABB9C', height: 3 },
                    }}
                >
                    <Tab icon={<TuneIcon />} iconPosition="start" label="Geral" />
                    <Tab icon={<StorefrontIcon />} iconPosition="start" label="Consultório" />
                    <Tab icon={<HubIcon />} iconPosition="start" label="Integrações" />
                    <Tab icon={<MessageIcon />} iconPosition="start" label="Mensagens" />
                </Tabs>

                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    {tab === 0 && <SecaoGeral parametros={parametros} handleChange={handleChange} />}
                    {tab === 1 && (
                        <SecaoConsultorio
                            parametros={parametros}
                            psicologos={psicologos}
                            handleChange={handleChange}
                            handleEnderecoChange={handleEnderecoChange}
                            handleBuscaCEP={handleBuscaCEP}
                            buscandoCep={buscandoCep}
                            logoPreview={logoBlobUrl}
                            logoInputRef={logoInputRef}
                            handleLogoChange={handleLogoChange}
                            uploadingLogo={uploadingLogo}
                        />
                    )}
                    {tab === 3 && <SecaoMensagens />}
                    {tab === 2 && (
                        <SecaoIntegracoes
                            parametros={parametros}
                            handleChange={handleChange}
                            evoStatus={evoStatus}
                            evoQrcode={evoQrcode}
                            evoLoading={evoLoading}
                            evoSendData={evoSendData}
                            setEvoSendData={setEvoSendData}
                            evoSending={evoSending}
                            carregarEvoQrCode={carregarEvoQrCode}
                            carregarEvoStatus={carregarEvoStatus}
                            enviarMensagemTeste={enviarMensagemTeste}
                            testeData={testeData}
                            setTesteData={setTesteData}
                            testeSending={testeSending}
                            enviarTesteNotificacao={enviarTesteNotificacao}
                        />
                    )}
                </Box>
            </Paper>

            {/* Sticky Save Bar */}
            <Paper
                elevation={3}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: { xs: 0, md: 240 },
                    right: 0,
                    px: 3, py: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    bgcolor: '#fff',
                    borderTop: '1px solid #eee',
                    zIndex: 10,
                }}
            >
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 4 }}>
                    Salvar Configurações
                </Button>
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={6000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
